import { type ReactNode } from 'react';
import { HelpTip } from '@/components/ui/help-tip';
import type { ProjectedGroupBonus } from '../types';
import { TierTrack } from './tier-track';
import { TeamGoalBar } from './team-goal-bar';
import { groupBonusStatus } from '../utils/group-bonus-status';

const HELP =
  'Si el equipo alcanza el objetivo semanal, todos los collectors desbloquean este bonus. El monto depende de tu media diaria de creadores: cuanto más alta tu media, más alto el tramo y el bonus.';

function fmtAvg(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0';
  return String(Math.round(n));
}

function Title({ headerRight }: { headerRight?: ReactNode }): ReactNode {
  return (
    <div className="flex items-start justify-between gap-sm">
      <p className="text-[10px] font-medium uppercase tracking-wider text-text-tertiary/60">
        Bonus de equipo
        <span className="ml-0.5 inline-block normal-case">
          <HelpTip text={HELP} />
        </span>
      </p>
      {headerRight}
    </div>
  );
}

function Avg({ value }: { value: number }): ReactNode {
  return (
    <div>
      <p className="whitespace-nowrap font-display text-2xl font-bold leading-none tabular-nums text-text-primary">
        {fmtAvg(value)}
        <span className="ml-1 text-[11px] font-normal text-text-tertiary">
          creadores / día
        </span>
      </p>
      <p className="mt-1 text-[10px] text-text-tertiary">
        Tu media esta semana
      </p>
    </div>
  );
}

type AmountProps = { amount: number; unlocked: boolean };

function Amount({ amount, unlocked }: AmountProps): ReactNode {
  const show = amount > 0;
  const tone = show && unlocked ? 'text-brand-red' : 'text-text-tertiary';
  return (
    <div className="text-right">
      <p
        className={`font-display text-2xl font-bold leading-none tabular-nums ${tone}`}
      >
        {show ? `+$${amount}` : '—'}
      </p>
      {unlocked && (
        <p className="mt-1 text-[10px] text-text-tertiary">Tier actual</p>
      )}
    </div>
  );
}

const TONES = {
  success: 'text-success',
  warning: 'text-brand-coral',
  muted: 'text-text-tertiary',
} as const;

function Status({ gb }: { gb: ProjectedGroupBonus }): ReactNode {
  const s = groupBonusStatus(gb);
  if (!s) return null;
  return <p className={`text-[11px] ${TONES[s.tone]}`}>{s.text}</p>;
}

function Body({ gb }: { gb: ProjectedGroupBonus }): ReactNode {
  return (
    <>
      <div className="mt-sm">
        <TeamGoalBar total={gb.week_total} goal={gb.week_goal} />
      </div>
      <div className="mt-base flex items-baseline justify-between">
        <Avg value={gb.daily_avg} />
        <Amount amount={gb.group_bonus_amount} unlocked={gb.unlocked} />
      </div>
      <div className="mt-md">
        <TierTrack tiers={gb.tiers} avg={gb.daily_avg} />
      </div>
      <div className="mt-sm">
        <Status gb={gb} />
      </div>
    </>
  );
}

type CardProps = {
  gb: ProjectedGroupBonus | undefined;
  headerRight?: ReactNode;
};

export function GroupBonusRaceCard({ gb, headerRight }: CardProps): ReactNode {
  if (!gb || gb.tiers.length === 0) return null;
  return (
    <div className="rounded-xl border border-white/[0.04] bg-white/[0.02] p-base">
      <Title {...(headerRight && { headerRight })} />
      <Body gb={gb} />
    </div>
  );
}
